import {useEffect, useRef, useState} from "react";
import PropTypes from "prop-types";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import {authService} from "../../service/auth/auth.service";

const SUBSCRIPTION_TYPES = [
    {
        key: "emailMarketing",
        statusField: "emailMarketingStatus",
        type: "EMAIL_MARKETING",
        label: "Email Marketing"
    },
    {
        key: "smsMarketing",
        statusField: "smsMarketingStatus",
        type: "SMS_MARKETING",
        label: "Text Message Marketing"
    },
    {
        key: "transactional",
        statusField: "smsTransactionalStatus",
        type: "TRANSACTIONAL",
        label: "Transactional Text Messaging"
    }
];

const IMPACT_MESSAGES = {
    emailMarketing: {
        subscribe: "The customer will start receiving promotional emails about sales, new products, and offers.",
        unsubscribe: "The customer will stop receiving promotional emails about sales, new products, and offers."
    },
    smsMarketing: {
        subscribe: "The customer will start receiving promotional text messages about sales, new products, and offers. Message and data rates may apply.",
        unsubscribe: "The customer will stop receiving promotional text messages about sales, new products, and offers."
    },
    transactional: {
        subscribe: "The customer will start receiving essential account and order text messages, such as verification codes, password resets, and order updates.",
        unsubscribe: "The customer will stop receiving essential account and order text messages, including verification codes, password resets, and order updates. This may affect their ability to complete actions like signing in or checking out."
    }
};

export default function Subscriptions({
                                          lookupData,
                                          profileId,
                                          showAlert,
                                          isActive
                                      }) {
    const [status, setStatus] = useState({});
    const [loading, setLoading] = useState(false);
    const [togglingKey, setTogglingKey] = useState(null);
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [pendingSubscription, setPendingSubscription] = useState(null);

    const showAlertRef = useRef(showAlert);
    useEffect(() => {
        showAlertRef.current = showAlert;
    }, [showAlert]);

    useEffect(() => {
        if (!isActive) return;

        if (!lookupData?.searchValue) {
            setStatus({});
            setEmail("");
            setPhone("");
            return;
        }

        setLoading(true);

        authService.getSubscriptionStatus(lookupData)
            .then((data) => {
                if (!data || data?.error) {
                    showAlertRef.current(
                        data?.error || "Failed to load subscription status",
                        "danger"
                    );
                    setStatus({});
                    return;
                }

                const subscriptionData = Array.isArray(data)
                    ? data[0]
                    : data;

                if (!subscriptionData) {
                    showAlertRef.current(
                        "No subscription data found",
                        "danger"
                    );
                    setStatus({});
                    return;
                }

                const mappedStatus = SUBSCRIPTION_TYPES.reduce(
                    (acc, subscription) => {
                        acc[subscription.key] =
                            subscriptionData[subscription.statusField] ===
                            "SUBSCRIBED";

                        return acc;
                    },
                    {}
                );

                setStatus(mappedStatus);
                setEmail(subscriptionData.email || "");
                setPhone(subscriptionData.phoneNumber || "");
            })
            .catch(() => {
                showAlertRef.current(
                    "Failed to load subscription status",
                    "danger"
                );
                setStatus({});
            })
            .finally(() => {
                setLoading(false);
            });
    }, [isActive, lookupData]);

    const handleToggleRequest = (subscription) => {
        if (!profileId) {
            showAlert(
                "No Klaviyo profile found for this customer",
                "danger"
            );
            return;
        }

        setPendingSubscription(subscription);
    };

    const closeConfirmModal = () => {
        setPendingSubscription(null);
    };

    const handleConfirmToggle = () => {
        if (pendingSubscription) {
            handleToggle(pendingSubscription);
        }

        setPendingSubscription(null);
    };

    const handleToggle = (subscription) => {
        const currentStatus = !!status?.[subscription.key];
        const subscribe = !currentStatus;

        setTogglingKey(subscription.key);

        authService.updateSubscription({
            profileId,
            channel: subscription.type,
            action: subscribe ? "SUBSCRIBE" : "UNSUBSCRIBE",
            email,
            phoneNumber: phone
        })
            .then((data) => {
                if (data && !data?.error) {
                    setStatus((prev) => ({
                        ...prev,
                        [subscription.key]: subscribe
                    }));

                    showAlert(
                        `${subscription.label} ${
                            subscribe ? "subscribed" : "unsubscribed"
                        } successfully!`,
                        "success"
                    );
                } else {
                    showAlert(
                        data?.error ||
                        `Failed to update ${subscription.label}`,
                        "danger"
                    );
                }
            })
            .catch(() => {
                showAlert(
                    `Failed to update ${subscription.label}`,
                    "danger"
                );
            })
            .finally(() => {
                setTogglingKey(null);
            });
    };

    return (
        <div>
            <h4
                id="sub_head"
                style={{
                    fontFamily: "A Love of Thunder",
                    color: "#007935",
                    marginBottom: "10px"
                }}
            >
                Communication Preferences
            </h4>

            {loading && <p>Loading subscription status...</p>}

            {!loading && (
                <>
                    <div
                        style={{
                            marginBottom: "24px",
                            padding: "16px 18px",
                            border: "1px solid #e8e8e3",
                            borderRadius: "12px",
                            background: "#fafaf7",
                            fontFamily: "Arial, sans-serif"
                        }}
                    >
                        <div
                            style={{
                                fontSize: "13px",
                                color: "#6b6f68",
                                lineHeight: 1.5,
                                marginBottom: "12px"
                            }}
                        >
                            Manage the customer's email and SMS communication preferences.
                        </div>

                        <div
                            style={{
                                fontSize: "13px",
                                color: "#444842",
                                lineHeight: 1.6
                            }}
                        >
                            <span style={{fontWeight: 600, color: "#23261f"}}>Marketing:</span>{" "}
                            Promotional emails and texts. Can be turned on or off anytime.
                            <br/>
                            <span style={{fontWeight: 600, color: "#23261f"}}>Transactional:</span>{" "}
                            Essential account and service messages, such as verification codes and password resets.
                        </div>
                    </div>

                    <div
                        style={{
                            background: "#fff",
                            borderRadius: "12px",
                            padding: "20px",
                            boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
                            display: "flex",
                            flexDirection: "column",
                            gap: "20px"
                        }}
                    >
                        {SUBSCRIPTION_TYPES.map((subscription) => {
                            const isSubscribed =
                                !!status?.[subscription.key];

                            const isToggling =
                                togglingKey === subscription.key;

                            return (
                                <div
                                    key={subscription.key}
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between"
                                    }}
                                >
                                    <span>{subscription.label}</span>

                                    <label className="switch" aria-label={`Toggle ${subscription.label}`}>
                                        <input
                                            type="checkbox"
                                            checked={isSubscribed}
                                            disabled={isToggling}
                                            onChange={() =>
                                                handleToggleRequest(subscription)
                                            }
                                        />

                                        <span className="slider"/>
                                    </label>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}

            <Modal show={!!pendingSubscription} onHide={closeConfirmModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title style={{width: "100%", textAlign: "center"}}>Confirm Subscription Change</Modal.Title>
                </Modal.Header>

                {pendingSubscription && (
                    <Modal.Body>
                        <p>
                            You are about to{" "}
                            <strong>
                                {status?.[pendingSubscription.key] ? "unsubscribe" : "subscribe"}
                            </strong>{" "}
                            this customer {status?.[pendingSubscription.key] ? "from" : "to"}{" "}
                            {pendingSubscription.label}.
                        </p>

                        <p>
                            {
                                IMPACT_MESSAGES[pendingSubscription.key][
                                    status?.[pendingSubscription.key] ? "unsubscribe" : "subscribe"
                                ]
                            }
                        </p>

                        <p style={{fontWeight: 600}}>
                            Confirm that you have obtained the customer's consent before proceeding.
                        </p>
                    </Modal.Body>
                )}

                <Modal.Footer style={{width: "100%", textAlign: "center"}}>
                    <Button variant="secondary" onClick={closeConfirmModal}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleConfirmToggle}>
                        Confirm
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}


Subscriptions.propTypes = {
    lookupData: PropTypes.shape({
        fieldType: PropTypes.string,
        searchValue: PropTypes.string
    }),
    profileId: PropTypes.string,
    showAlert: PropTypes.func.isRequired,
    isActive: PropTypes.bool
};

Subscriptions.defaultProps = {
    isActive: false
};
